import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Loader2, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("pa_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [soumissions, setSoumissions] = useState([]);
  const [fetching, setFetching] = useState(false);

  const fetchSoumissions = useCallback(async (tk) => {
    setFetching(true);
    try {
      const res = await axios.get(`${API}/soumissions`, { headers: { Authorization: `Bearer ${tk}` } });
      setSoumissions(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("pa_admin_token");
        setToken("");
        toast.error("Session expirée, veuillez vous reconnecter.");
      } else {
        toast.error("Impossible de charger les demandes.");
      }
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchSoumissions(token);
  }, [token, fetchSoumissions]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("pa_admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Connexion réussie.");
    } catch (e) {
      toast.error("Mot de passe incorrect.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("pa_admin_token");
    setToken("");
    setSoumissions([]);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="glass-gold rounded-2xl p-10 w-full max-w-md">
          <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold">
            <Lock size={22} />
          </span>
          <h1 className="font-display font-bold text-2xl text-center mb-2">Espace administrateur</h1>
          <p className="text-sm text-white/50 text-center mb-8 font-light">Premium Autoshine — demandes de soumission</p>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-5">
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input"
              placeholder="Mot de passe administrateur"
            />
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-gold py-3.5 font-display font-bold uppercase tracking-widest text-sm text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Se connecter"}
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white px-6 lg:px-10 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="font-display font-black uppercase tracking-tight text-3xl mb-1">
              Demandes de <span className="gold-gradient-text">soumission</span>
            </h1>
            <p className="text-sm text-white/50 flex items-center gap-2">
              <ShieldCheck size={14} className="text-gold" /> {soumissions.length} demande{soumissions.length !== 1 ? "s" : ""} reçue{soumissions.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => fetchSoumissions(token)}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs uppercase tracking-widest text-white/70 hover:border-gold/50 hover:text-gold transition-colors duration-300"
            >
              <RefreshCw size={14} className={fetching ? "animate-spin" : ""} /> Actualiser
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs uppercase tracking-widest text-white/70 hover:border-red-400/50 hover:text-red-300 transition-colors duration-300"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>

        {fetching && soumissions.length === 0 ? (
          <div className="flex justify-center py-24">
            <Loader2 size={32} className="animate-spin text-gold" />
          </div>
        ) : soumissions.length === 0 ? (
          <div data-testid="admin-empty-state" className="glass-panel rounded-2xl p-16 text-center text-white/50 text-sm">
            Aucune demande de soumission pour le moment.
          </div>
        ) : (
          <div className="space-y-4">
            {soumissions.map((s) => (
              <motion.article
                key={s.id}
                data-testid={`admin-soumission-${s.id.slice(0, 8)}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-panel rounded-xl p-6 hover:border-gold/30 transition-[border-color] duration-300"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                  <div>
                    <h2 className="font-display font-bold text-lg">{s.nom}</h2>
                    <p className="text-xs uppercase tracking-[0.2em] text-gold">{s.service}</p>
                  </div>
                  <span className="text-xs text-white/40">
                    {new Date(s.created_at).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/70 mb-3">
                  <a data-testid={`admin-call-${s.id.slice(0, 8)}`} href={`tel:${s.telephone.replace(/\D/g, "")}`} className="flex items-center gap-2 hover:text-gold transition-colors duration-300">
                    <Phone size={13} className="text-gold" /> {s.telephone}
                  </a>
                  {s.courriel && <span>{s.courriel}</span>}
                  {s.vehicule && <span className="text-white/50">{s.vehicule}</span>}
                </div>
                {s.message && <p className="text-sm text-white/55 font-light border-t border-white/10 pt-3">{s.message}</p>}
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
